import { useEffect, useRef, useState } from 'react'
import type { PulseMessage, PulseVisitor } from '../shared/types'

export interface LiveState {
	online: number | null
	recent: PulseVisitor[]
	/** The most recent join since the page loaded, for "NEW CHALLENGER" toasts. */
	lastJoin: PulseVisitor | null
}

/** Keeps a WebSocket open to /live and reports who else is playing. */
export function useLive(enabled = true): LiveState {
	const [state, setState] = useState<LiveState>({ online: null, recent: [], lastJoin: null })
	const attempts = useRef(0)

	useEffect(() => {
		if (!enabled) return
		let ws: WebSocket | null = null
		let closed = false
		let retry = 0
		let keepalive = 0

		const connect = () => {
			const proto = location.protocol === 'https:' ? 'wss' : 'ws'
			ws = new WebSocket(`${proto}://${location.host}/live`)
			ws.onopen = () => {
				attempts.current = 0
				keepalive = window.setInterval(() => ws?.readyState === WebSocket.OPEN && ws.send('ping'), 30000)
			}
			ws.onmessage = (ev) => {
				if (ev.data === 'pong') return
				let msg: PulseMessage
				try {
					msg = JSON.parse(ev.data)
				} catch {
					return
				}
				setState((s) => {
					if (msg.type === 'state') return { ...s, online: msg.online, recent: msg.recent }
					if (msg.type === 'join') return { online: msg.online, recent: [msg.visitor, ...s.recent].slice(0, 12), lastJoin: msg.visitor }
					return { ...s, online: msg.online }
				})
			}
			ws.onclose = () => {
				clearInterval(keepalive)
				if (closed) return
				attempts.current++
				retry = window.setTimeout(connect, Math.min(30000, 1000 * 2 ** attempts.current))
			}
			ws.onerror = () => ws?.close()
		}
		connect()
		return () => {
			closed = true
			clearTimeout(retry)
			clearInterval(keepalive)
			ws?.close()
		}
	}, [enabled])

	return state
}
